import { useTranslation } from "react-i18next";
import { useCartStore } from "@/components/cart/store";
import { useAddToCart } from "@/hooks/use-add-to-cart";
import { alertUtils } from "@/lib/alert-utils";
import type { Product } from "@/lib/products";
import { getRemainingStock } from "@/lib/storefront/catalog-filters";
import { getQuantityByProductId } from "@/lib/storefront/cart-lines";
import { useCartSheet } from "./use-cart-sheet";

export const AddToCartResults = {
  added: "added",
  needsOptions: "needs_options",
  blocked: "blocked",
} as const;

export type AddToCartResult =
  (typeof AddToCartResults)[keyof typeof AddToCartResults];

/**
 * Quick add from the catalogue grid: the shared add-to-cart checks, plus the
 * stock. A product that needs a variant or option choice is reported back so
 * the grid can send the customer to its page instead.
 */
export function useStorefrontAddToCart() {
  const { t } = useTranslation("translation");
  const { addToCart } = useAddToCart();
  const setCartOpen = useCartSheet((state) => state.setOpen);

  const quickAdd = async (product: Product): Promise<AddToCartResult> => {
    const items = useCartStore.getState().cart.items;
    const inCart = getQuantityByProductId(items).get(product.id) ?? 0;
    const remaining = getRemainingStock(product, inCart);

    if (remaining !== undefined && remaining < 1) {
      alertUtils.error(
        t("Storefront.stock_limit_reached"),
        t("Storefront.sold_out")
      );
      return AddToCartResults.blocked;
    }

    const needsDialog = await addToCart(product, { quantity: 1 });
    if (needsDialog) return AddToCartResults.needsOptions;

    // addToCart returns false both when it added and when it refused with an
    // alert of its own; only an actual add grows the cart.
    const added =
      (getQuantityByProductId(useCartStore.getState().cart.items).get(
        product.id
      ) ?? 0) > inCart;
    if (!added) return AddToCartResults.blocked;

    setCartOpen(true);
    return AddToCartResults.added;
  };

  return { quickAdd };
}
